import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Save,
  Lock,
  Download,
  RefreshCw,
  Plus,
  Link2,
  FileText,
  ChevronLeft,
  Pencil,
  Trash2,
  X,
} from 'lucide-react'
import { useAppStore, useFilteredItems, useFolderTree } from '../store'
import { api } from '../lib/api'
import { encryptVaultData, deriveCacheKey, fromBase64 } from '../lib/crypto'
import { cacheVaultData } from '../lib/cache'
import { exportVaultAsZip, downloadZip } from '../lib/export'
import { VaultItem } from '../config'
import { gradientStyle, groupVaultId } from '../lib/folderColors'
import { toast } from '../store/toast'
import { ItemEditor } from './ItemEditor'
import { FolderEditor } from './FolderEditor'
import { TagEditor } from './TagEditor'
import { FolderWallet } from './vault/FolderWallet'
import { ItemGrid } from './vault/ItemGrid'
import { ItemDetail } from './vault/ItemDetail'
import { ConfirmDialog, ConfirmOptions } from './ui/ConfirmDialog'

type TypeFilter = 'all' | 'bookmark' | 'note'
const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'bookmark', label: 'Links' },
  { id: 'note', label: 'Notes' },
]

export function VaultBrowser() {
  const navigate = useNavigate()
  const vault = useAppStore((s) => s.unlockedVault)
  const setUnlockedVault = useAppStore((s) => s.setUnlockedVault)
  const updateVaultData = useAppStore((s) => s.updateVaultData)
  const markVaultClean = useAppStore((s) => s.markVaultClean)
  const selectedFolderId = useAppStore((s) => s.selectedFolderId)
  const setSelectedFolderId = useAppStore((s) => s.setSelectedFolderId)
  const selectedTagIds = useAppStore((s) => s.selectedTagIds)
  const toggleTagFilter = useAppStore((s) => s.toggleTagFilter)
  const setSelectedTagIds = useAppStore((s) => s.setSelectedTagIds)
  const searchQuery = useAppStore((s) => s.searchQuery)
  const setSearchQuery = useAppStore((s) => s.setSearchQuery)

  const filtered = useFilteredItems()
  const folderTree = useFolderTree()

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [detailItem, setDetailItem] = useState<VaultItem | null>(null)
  const [editingItem, setEditingItem] = useState<VaultItem | null>(null)
  const [creatingItem, setCreatingItem] = useState<'bookmark' | 'note' | null>(
    null,
  )
  const [editingFolder, setEditingFolder] = useState<string | null>(null)
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [editingTag, setEditingTag] = useState<string | null>(null)
  const [creatingTag, setCreatingTag] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmOptions | null>(null)
  // Phone only: the folder panel opens over the wallet.
  const [panelOpen, setPanelOpen] = useState(false)
  const [panelClosing, setPanelClosing] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  const items = useMemo(
    () =>
      typeFilter === 'all'
        ? filtered
        : filtered.filter((i) => i.type === typeFilter),
    [filtered, typeFilter],
  )
  const folderNames = useMemo(
    () => new Map((vault?.data.folders ?? []).map((f) => [f.id, f.name])),
    [vault?.data.folders],
  )

  // Keyboard: ⌘/Ctrl+S saves, ⌘/Ctrl+K focuses search.
  const saveRef = useRef<() => void>(() => {})
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.key.toLowerCase() === 's') {
        e.preventDefault()
        saveRef.current()
      } else if (e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (window.innerWidth < 1024) setPanelOpen(true)
        setTimeout(() => searchRef.current?.focus(), 50)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Warn before leaving with unsaved changes.
  const isDirty = !!vault?.isDirty
  useEffect(() => {
    if (!isDirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])

  if (!vault) return null

  const handleSave = async () => {
    if (!vault.isDirty || saving) return
    setSaving(true)
    try {
      const jsonData = JSON.stringify(vault.data)
      const encryptedData = await encryptVaultData(jsonData, vault.key)
      await api.uploadVaultData(vault.id, vault.verificationTag, encryptedData)

      const salt = fromBase64(vault.meta.salt)
      const cacheKey = await deriveCacheKey(vault.key, salt)
      await cacheVaultData(vault.id, encryptedData, cacheKey)

      markVaultClean()
      toast('Vault encrypted and saved', 'success')
    } catch (error) {
      console.error('Failed to save vault:', error)
      toast('Could not save the vault. Please try again.', 'error')
    } finally {
      setSaving(false)
    }
  }
  saveRef.current = handleSave

  const lockNow = () => {
    setUnlockedVault(null)
    navigate('/')
  }

  const handleLockVault = () => {
    if (vault.isDirty) {
      setConfirm({
        title: 'Lock with unsaved changes?',
        body: 'Changes you haven’t saved will be lost when the vault locks.',
        confirmLabel: 'Lock anyway',
        danger: true,
        onConfirm: lockNow,
      })
      return
    }
    lockNow()
  }

  const handleDeleteItem = (item: VaultItem) => {
    setConfirm({
      title: `Delete “${item.title}”?`,
      body: 'It will be removed from this vault when you save.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => {
        updateVaultData((data) => ({
          ...data,
          items: data.items.filter((i) => i.id !== item.id),
        }))
        setDetailItem(null)
      },
    })
  }

  const handleDeleteFolder = (folderId: string) => {
    const hasItems = vault.data.items.some((item) => item.folderId === folderId)
    const hasSubfolders = vault.data.folders.some(
      (f) => f.parentId === folderId,
    )
    if (hasItems || hasSubfolders) {
      toast('Move or delete what’s inside this folder first.', 'error')
      return
    }
    const name = folderNames.get(folderId) ?? 'this folder'
    setConfirm({
      title: `Delete “${name}”?`,
      confirmLabel: 'Delete folder',
      danger: true,
      onConfirm: () => {
        updateVaultData((data) => ({
          ...data,
          folders: data.folders.filter((f) => f.id !== folderId),
        }))
        if (selectedFolderId === folderId) setSelectedFolderId(null)
      },
    })
  }

  const handleDeleteTag = (tagId: string) => {
    setConfirm({
      title: 'Delete this tag?',
      body: 'Items keep their content; they just lose this tag.',
      confirmLabel: 'Delete tag',
      danger: true,
      onConfirm: () => {
        updateVaultData((data) => ({
          ...data,
          tags: data.tags.filter((t) => t.id !== tagId),
          items: data.items.map((item) => ({
            ...item,
            tagIds: item.tagIds.filter((id) => id !== tagId),
          })),
        }))
        setSelectedTagIds(selectedTagIds.filter((id) => id !== tagId))
        setEditingTag(null)
      },
    })
  }

  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const zipData = exportVaultAsZip(vault.id, vault.data)
      downloadZip(vault.id, zipData)
    } catch (error) {
      console.error('Failed to export:', error)
      toast('Export failed', 'error')
    } finally {
      setExporting(false)
    }
  }

  const selectFolder = (id: string | null) => {
    setSelectedFolderId(id)
    if (window.innerWidth < 1024) {
      setPanelClosing(false)
      setPanelOpen(true)
    }
  }

  const closePanel = () => {
    setPanelOpen(false)
    setPanelClosing(true)
    setTimeout(() => setPanelClosing(false), 300)
  }

  const folderName = selectedFolderId
    ? (folderNames.get(selectedFolderId) ?? 'Folder')
    : 'All items'
  const hasFilters =
    !!searchQuery.trim() || selectedTagIds.length > 0 || typeFilter !== 'all'
  const gridKey = `${selectedFolderId}|${typeFilter}|${selectedTagIds.join(',')}`
  const typeIndex = TYPE_FILTERS.findIndex((t) => t.id === typeFilter)

  const panelMobile = panelOpen
    ? 'max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:flex max-lg:overflow-y-auto max-lg:rounded-none max-lg:border-0 max-lg:bg-[rgba(10,10,18,0.9)] max-lg:animate-[panelUp_.6s_var(--spring)_both]'
    : panelClosing
      ? 'max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:flex max-lg:rounded-none max-lg:border-0 max-lg:bg-[rgba(10,10,18,0.9)] max-lg:animate-[panelDown_.3s_cubic-bezier(.4,0,1,1)_both]'
      : 'max-lg:hidden'

  return (
    <div className="lg:grid lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start lg:gap-8">
      {/* ------------------------------------------------ Left: wallet */}
      <aside className="flex flex-col gap-6 lg:sticky lg:top-[88px] lg:max-h-[calc(100dvh-112px)] lg:overflow-y-auto lg:py-1 lg:pl-1 lg:pr-3">
        {/* Vault status */}
        <div className="panel flex items-center gap-3 !rounded-2xl p-3 pl-4 animate-fade-up">
          <span
            className={`dot ${vault.isDirty ? 'bg-[#F2CC6B]' : 'animate-slow-pulse bg-[#5EE0A6]'}`}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate font-mono text-[13px] tracking-wide">
              {groupVaultId(vault.id)}
            </div>
            <div className="text-[12.5px] text-faint">
              {vault.isDirty ? 'Unsaved changes' : 'Saved · encrypted'}
            </div>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={!vault.isDirty || saving}
            className={
              vault.isDirty
                ? 'btn-primary !min-h-[40px] !px-3.5'
                : 'btn-secondary !min-h-[40px] !px-3.5'
            }
            title="Save (⌘S)"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving' : 'Save'}
          </button>
        </div>

        <FolderWallet
          folders={folderTree}
          items={vault.data.items}
          selectedFolderId={selectedFolderId}
          onSelect={selectFolder}
          onCreateFolder={() => setCreatingFolder(true)}
          onEditFolder={(id) => setEditingFolder(id)}
          onDeleteFolder={handleDeleteFolder}
        />

        {/* Tags */}
        <section
          className="animate-fade-up"
          style={{ animationDelay: '250ms' }}
        >
          <div className="mb-3 flex items-center justify-between px-1">
            <h2 className="text-[13px] font-semibold text-faint">Tags</h2>
            <button
              type="button"
              className="icon-btn !h-9 !w-9 !rounded-xl"
              onClick={() => setCreatingTag(true)}
              aria-label="New tag"
              title="New tag"
            >
              <Plus className="h-[18px] w-[18px]" />
            </button>
          </div>
          {vault.data.tags.length === 0 ? (
            <p className="px-1 text-sm text-faint">No tags yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {vault.data.tags.map((tag) => {
                const on = selectedTagIds.includes(tag.id)
                return (
                  <span key={tag.id} className="inline-flex items-center">
                    <button
                      type="button"
                      className="chip !min-h-[36px]"
                      aria-pressed={on}
                      onClick={() => toggleTagFilter(tag.id)}
                    >
                      <span className="dot" style={{ background: tag.color }} />
                      {tag.name}
                    </button>
                    {on && (
                      <button
                        type="button"
                        className="icon-btn !h-9 !w-9 !rounded-xl animate-fade-in"
                        aria-label={`Edit tag ${tag.name}`}
                        onClick={() => setEditingTag(tag.id)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </span>
                )
              })}
            </div>
          )}
        </section>

        <div
          className="flex gap-2 animate-fade-up"
          style={{ animationDelay: '300ms' }}
        >
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="btn-secondary flex-1"
          >
            {exporting ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export
          </button>
          <button
            type="button"
            onClick={handleLockVault}
            className="btn-secondary flex-1"
          >
            <Lock className="h-4 w-4" />
            Lock
          </button>
        </div>
      </aside>

      {/* ------------------------------------------------ Right: items */}
      <section
        aria-label={folderName}
        style={gradientStyle(selectedFolderId)}
        className={`panel relative flex min-h-[70dvh] flex-col overflow-hidden backdrop-blur-2xl lg:animate-fade-up ${panelMobile}`}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-[150px] left-[8%] right-[8%] h-[260px] rounded-[50%] opacity-50 blur-[80px] transition-colors duration-700"
          style={{ backgroundColor: 'var(--c1)' }}
        />

        <header className="relative flex flex-col gap-4 px-4 pb-4 pt-3 sm:px-7 sm:pt-7">
          <div className="flex items-center gap-1 lg:hidden">
            <button
              type="button"
              className="link-btn -ml-2 font-semibold"
              onClick={closePanel}
            >
              <ChevronLeft className="h-5 w-5" />
              Folders
            </button>
            <span className="flex-1" />
            {selectedFolderId && (
              <>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Rename folder"
                  onClick={() => setEditingFolder(selectedFolderId)}
                >
                  <Pencil className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Delete folder"
                  onClick={() => handleDeleteFolder(selectedFolderId)}
                >
                  <Trash2 className="h-[18px] w-[18px]" />
                </button>
              </>
            )}
            {vault.isDirty && (
              <button
                type="button"
                className="icon-btn !text-vault-300"
                aria-label="Save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <RefreshCw className="h-[18px] w-[18px] animate-spin" />
                ) : (
                  <Save className="h-[18px] w-[18px]" />
                )}
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-muted">
                <span
                  aria-hidden="true"
                  className="h-2.5 w-2.5 rounded-[3px] bg-gradient-to-br from-[var(--c1)] to-[var(--c2)]"
                />
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </p>
              <h1
                key={folderName}
                className="truncate font-display text-[30px] font-semibold leading-tight tracking-tight animate-fade-up sm:text-[40px]"
              >
                {folderName}
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setCreatingItem('bookmark')}
              >
                <Link2 className="h-4 w-4" />
                <span>Add link</span>
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setCreatingItem('note')}
              >
                <FileText className="h-4 w-4" />
                <span>Add note</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="glass flex min-h-[46px] flex-1 items-center gap-2.5 rounded-2xl px-4 text-muted transition-colors focus-within:border-vault-500/50 focus-within:text-white">
              <Search className="h-[18px] w-[18px] flex-none" />
              <span className="sr-only">Search this vault</span>
              <input
                ref={searchRef}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
                placeholder="Search titles, links and notes"
                className="min-w-0 flex-1 bg-transparent text-[15px] text-white outline-none placeholder:text-faint"
              />
              {searchQuery ? (
                <button
                  type="button"
                  className="-mr-2 grid h-9 w-9 place-items-center rounded-xl hover:bg-white/10"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <kbd className="hidden rounded-md border border-white/10 bg-white/5 px-1.5 font-mono text-[11px] text-faint lg:inline">
                  ⌘K
                </kbd>
              )}
            </label>
            <div
              className="seg glass"
              role="group"
              aria-label="Item type"
              style={
                {
                  '--i': typeIndex,
                  '--n': TYPE_FILTERS.length,
                } as React.CSSProperties
              }
            >
              <span className="seg-pill" aria-hidden="true" />
              {TYPE_FILTERS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="seg-btn min-h-[40px] sm:min-w-[76px]"
                  aria-pressed={typeFilter === t.id}
                  onClick={() => setTypeFilter(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="relative flex-1 px-4 pb-6 sm:px-7 sm:pb-8">
          {items.length > 0 ? (
            <ItemGrid
              key={gridKey}
              items={items}
              tags={vault.data.tags}
              folderNames={folderNames}
              showFolder={selectedFolderId === null}
              onOpen={setDetailItem}
            />
          ) : (
            <div className="grid place-items-center px-4 py-16 text-center animate-fade-up">
              <div className="max-w-sm">
                <span className="kind-ic note mx-auto mb-4 !h-14 !w-14 !rounded-2xl">
                  {hasFilters ? (
                    <Search className="h-6 w-6" />
                  ) : (
                    <FileText className="h-6 w-6" />
                  )}
                </span>
                <h2 className="font-display text-xl font-semibold">
                  {hasFilters ? 'Nothing matches' : 'Nothing here yet'}
                </h2>
                <p className="mt-1.5 text-muted">
                  {hasFilters
                    ? 'Try a different search, tag or type.'
                    : 'Add a link or a note — it’s encrypted before it leaves this device.'}
                </p>
                {hasFilters && (
                  <button
                    type="button"
                    className="btn-secondary mt-5"
                    onClick={() => {
                      setSearchQuery('')
                      setSelectedTagIds([])
                      setTypeFilter('all')
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ------------------------------------------------ Overlays */}
      {detailItem && (
        <ItemDetail
          item={detailItem}
          tags={vault.data.tags}
          folderName={
            detailItem.folderId
              ? (folderNames.get(detailItem.folderId) ?? null)
              : null
          }
          onClose={() => setDetailItem(null)}
          onEdit={() => {
            setEditingItem(detailItem)
            setDetailItem(null)
          }}
          onDelete={() => handleDeleteItem(detailItem)}
        />
      )}

      {(creatingItem || editingItem) && (
        <ItemEditor
          item={editingItem}
          type={creatingItem || editingItem?.type || 'bookmark'}
          onClose={() => {
            setCreatingItem(null)
            setEditingItem(null)
          }}
        />
      )}

      {(creatingFolder || editingFolder) && (
        <FolderEditor
          folderId={editingFolder}
          onClose={() => {
            setCreatingFolder(false)
            setEditingFolder(null)
          }}
        />
      )}

      {(creatingTag || editingTag) && (
        <TagEditor
          tagId={editingTag}
          onClose={() => {
            setCreatingTag(false)
            setEditingTag(null)
          }}
          onDelete={editingTag ? () => handleDeleteTag(editingTag) : undefined}
        />
      )}

      {confirm && (
        <ConfirmDialog options={confirm} onClose={() => setConfirm(null)} />
      )}
    </div>
  )
}
